import { Div, Flex, Spacer, Text } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { useSubscriptions } from 'src/hooks'
import {
  LEGACY_31,
  LEGACY_39,
  LEGACY_980_1,
  LEGACY_980_2,
} from 'src/marketing/plans/constants'
import {
  ResponsiveColumn,
  ResponsiveRow,
  ResponsiveTable,
} from 'src/points_conversion/components/ResponsiveTable/ResponsiveTable'
import {
  activePlans,
  legacyPlans,
  tableFeatures,
} from 'src/points_conversion/data/pointsConversionData'
import { FeatureSet } from 'src/points_conversion/data/type'

export const PricingTable = () => {
  const subscription = useSubscriptions()
  const allPlans = { ...legacyPlans, ...activePlans }

  const getCurrentPlanId = () => {
    const stripeId = subscription.currentPlan.stripeId
    if (stripeId === 'legacy') {
      const activePlanSku = subscription.allSubscriptions
        .map(subscription => subscription.sku)
        .filter(
          sku =>
            sku === LEGACY_39 ||
            sku === LEGACY_980_1 ||
            sku === LEGACY_980_2 ||
            sku === LEGACY_31,
        )
      const [legacyPlanSku] = activePlanSku
      return legacyPlanSku === LEGACY_980_2
        ? LEGACY_980_1
        : legacyPlanSku === LEGACY_31
        ? LEGACY_39
        : legacyPlanSku
    }
    return stripeId
  }

  const currentPlan = getCurrentPlanId()
  const [currentPlanData] = (Object.keys(allPlans) as Array<
    keyof typeof allPlans
  >)
    .filter(key => {
      const plan = allPlans[key]

      return plan.sku === currentPlan
    })
    .map(key => allPlans[key])

  const planColumns = (Object.keys(activePlans) as Array<
    keyof typeof activePlans
  >).map(planKey => {
    const id = activePlans[planKey].id

    return (
      <ResponsiveColumn key={id} backgroundColor="#8B8B8B" heading={planKey}>
        {(Object.keys(activePlans[planKey].tableFeatures) as Array<
          keyof FeatureSet
        >).map((featureKey, index) => {
          const feature = activePlans[planKey].tableFeatures[featureKey]

          const mainFeature =
            feature.main === 'excluded' ? (
              <Icon name="x" color="#e6525d" size={20} />
            ) : feature.main === 'included' ? (
              <Icon name="check" color="#20966f" />
            ) : feature.main === 'included_with_stamps' ? (
              <Icon name="double_check" color="#20966f" />
            ) : (
              <Text
                type="caption"
                content={currentPlanData.name}
                style={{ fontWeight: 600, color: '#606060' }}
              />
            )

          const secondaryFeature = feature.secondary?.content

          return (
            <ResponsiveRow
              key={featureKey}
              alignment="center"
              backgroundColor={index % 2 ? '#f8f8f8' : '#fcfcfc'}
            >
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {mainFeature}
                {secondaryFeature && (
                  <>
                    <Spacer space="x1" orientation="vertical" />
                    <Text
                      type="footnote"
                      content={secondaryFeature}
                      style={{
                        fontSize: '8px',
                        color: '#676767',
                      }}
                      whiteSpace="nowrap"
                    />
                  </>
                )}
              </div>
            </ResponsiveRow>
          )
        })}
      </ResponsiveColumn>
    )
  })

  const titleColumn = (
    <ResponsiveColumn hasTitleRows={true}>
      {Object.keys(tableFeatures).map((featureKey, index) => (
        <ResponsiveRow
          key={featureKey}
          backgroundColor={index % 2 ? '#f8f8f8' : '#fcfcfc'}
        >
          {tableFeatures[featureKey as keyof typeof tableFeatures]}
        </ResponsiveRow>
      ))}
    </ResponsiveColumn>
  )

  const columns = [titleColumn, ...planColumns]

  return (
    <>
      <div style={{ overflowY: 'auto' }}>
        <Div
          width="100%"
          justifyContent="center"
          display="flex"
          flexWrap="wrap"
        >
          <Flex overflowX="auto" inset={{ bottom: '20px' }}>
            <ResponsiveTable>{columns}</ResponsiveTable>
          </Flex>
          <Spacer space="x2" orientation="vertical" />
        </Div>
      </div>
    </>
  )
}
